import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API_URL } from '../config';

export interface TimesheetSourceTicket {
  key: string;
  summary: string;
  hours: number;
  /** day (1–31) → hours */
  by_day?: { [day: number]: number };
}

interface TimesheetRow {
  key: string;
  summary: string;
  hours: number;
  rate: number;
  amount: number;
}

interface TimesheetModalProps {
  year: number;
  month: number;
  tickets: TimesheetSourceTicket[];
  personName?: string;
  onClose: () => void;
  onCreateInvoice: (preview: {
    rows: TimesheetRow[];
    totalHours: number;
    totalAmount: number;
    hourlyRate: number;
    suggestedItem: { name: string; quantity: number; price_per_unit: string };
    dateCreated: string;
    dateDue: string;
    defaults: {
      client_id?: number | null;
      supplier_id?: number | null;
      bank_account_id?: number | null;
    };
  }) => void;
}

const monthNames = [
  'Leden', 'Únor', 'Březen', 'Duben', 'Květen', 'Červen',
  'Červenec', 'Srpen', 'Září', 'Říjen', 'Listopad', 'Prosinec',
];

const formatMoney = (n: number) =>
  n.toLocaleString('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' Kč';

const formatHours = (n: number) =>
  `${n.toLocaleString('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}h`;

const TimesheetModal: React.FC<TimesheetModalProps> = ({
  year,
  month,
  tickets,
  personName,
  onClose,
  onCreateInvoice,
}) => {
  const [hourlyRate, setHourlyRate] = useState(1000);
  const [rows, setRows] = useState<TimesheetRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewMeta, setPreviewMeta] = useState<any>(null);

  const period = `${String(month).padStart(2, '0')}/${year}`;

  const ticketPayload = useMemo(
    () =>
      tickets.map((t) => ({
        key: t.key,
        summary: t.summary,
        hours: t.hours,
        by_day: t.by_day || {},
      })),
    [tickets]
  );

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const settingsRes = await axios.get(`${API_URL}/api/invoice/settings`);
        const rate = settingsRes.data?.hourly_rate ?? 1000;
        setHourlyRate(rate);

        const res = await axios.post(`${API_URL}/api/invoice/timesheet/preview`, {
          year,
          month,
          hourly_rate: rate,
          person_name: personName,
          tickets: ticketPayload,
        });
        setRows(res.data.rows || []);
        setPreviewMeta(res.data);
      } catch (err: any) {
        setError(err.response?.data?.detail || err.message || 'Nepodařilo se načíst timesheet');
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, [year, month, ticketPayload, personName]);

  const rebuild = async (rate: number) => {
    setHourlyRate(rate);
    try {
      const res = await axios.post(`${API_URL}/api/invoice/timesheet/preview`, {
        year,
        month,
        hourly_rate: rate,
        person_name: personName,
        tickets: ticketPayload,
      });
      setRows(res.data.rows || []);
      setPreviewMeta(res.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message);
    }
  };

  const totals = useMemo(() => {
    const hours = rows.reduce((s, r) => s + r.hours, 0);
    const amount = rows.reduce((s, r) => s + r.amount, 0);
    return { hours: Math.round(hours * 100) / 100, amount: Math.round(amount * 100) / 100 };
  }, [rows]);

  const downloadPdf = async () => {
    setDownloadingPdf(true);
    setError(null);
    try {
      const res = await axios.post(
        `${API_URL}/api/invoice/timesheet/pdf`,
        {
          year,
          month,
          hourly_rate: hourlyRate,
          person_name: personName,
          tickets: ticketPayload,
        },
        { responseType: 'blob' }
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `timesheet-${year}-${String(month).padStart(2, '0')}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      let msg = 'Stažení PDF selhalo';
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const parsed = JSON.parse(text);
          msg = parsed.detail || msg;
        } catch {
          /* ignore */
        }
      } else if (err.response?.data?.detail) {
        msg = err.response.data.detail;
      }
      setError(msg);
    } finally {
      setDownloadingPdf(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="bg-white dark:bg-slate-900 rounded-2xl shadow-panel border border-slate-200 dark:border-slate-700 w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-slate-100 dark:border-slate-700 flex items-start justify-between gap-3">
          <div>
            <h3 className="text-lg font-extrabold text-slate-900 dark:text-white">
              Timesheet z Jiry
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Období {monthNames[month - 1]} {year} · {period}
              {personName ? ` · ${personName}` : ''}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700 dark:hover:text-white text-xl leading-none px-1"
          >
            ×
          </button>
        </div>

        <div className="px-5 py-3 border-b border-slate-100 dark:border-slate-700 flex items-center gap-3 flex-wrap">
          <label className="text-xs font-semibold text-slate-600 dark:text-slate-300 flex items-center gap-2">
            Hodinová sazba
            <input
              type="number"
              min={0}
              step={50}
              value={hourlyRate}
              onChange={(e) => rebuild(Number(e.target.value) || 0)}
              className="w-28 px-2 py-1 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-transparent dark:text-white"
            />
            <span className="text-slate-400">Kč/h</span>
          </label>
          <button
            type="button"
            onClick={downloadPdf}
            disabled={rows.length === 0 || downloadingPdf}
            className="ml-auto text-xs font-semibold px-3 py-1.5 rounded-lg bg-slate-900 text-white hover:bg-slate-800 dark:bg-white dark:text-slate-900 disabled:opacity-40"
          >
            {downloadingPdf ? 'Generuji PDF…' : 'Stáhnout PDF'}
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-3">
          {loading ? (
            <div className="text-sm text-slate-400 py-8 text-center">Načítám…</div>
          ) : error ? (
            <div className="text-sm text-red-600 py-4">{error}</div>
          ) : rows.length === 0 ? (
            <div className="text-sm text-slate-400 py-8 text-center italic">
              V tomto měsíci nejsou žádné zalogované hodiny
            </div>
          ) : (
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500 border-b border-slate-200 dark:border-slate-700">
                  <th className="py-2 pr-2">Ticket</th>
                  <th className="py-2 pr-2">Description</th>
                  <th className="py-2 pr-2 text-right">Hours</th>
                  <th className="py-2 pr-2 text-right">Rate</th>
                  <th className="py-2 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.key} className="border-b border-slate-100 dark:border-slate-800">
                    <td className="py-2 pr-2 font-mono font-bold text-complic-700 dark:text-complic-300 whitespace-nowrap">
                      {r.key}
                    </td>
                    <td className="py-2 pr-2 text-slate-700 dark:text-slate-200 truncate max-w-[14rem]" title={r.summary}>
                      {r.summary}
                    </td>
                    <td className="py-2 pr-2 text-right tabular-nums">{formatHours(r.hours)}</td>
                    <td className="py-2 pr-2 text-right tabular-nums text-slate-500">
                      {r.rate.toLocaleString('cs-CZ')}
                    </td>
                    <td className="py-2 text-right tabular-nums font-semibold">{formatMoney(r.amount)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="font-bold">
                  <td className="pt-3" colSpan={2}>
                    Celkem
                  </td>
                  <td className="pt-3 text-right tabular-nums">{formatHours(totals.hours)}</td>
                  <td className="pt-3" />
                  <td className="pt-3 text-right tabular-nums text-complic-700 dark:text-complic-300">
                    {formatMoney(totals.amount)}
                  </td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>

        <div className="px-5 py-4 border-t border-slate-100 dark:border-slate-700 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-xl border border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-800"
          >
            Zavřít
          </button>
          <button
            type="button"
            disabled={!previewMeta || rows.length === 0}
            onClick={() => {
              if (!previewMeta) return;
              onCreateInvoice({
                rows,
                totalHours: previewMeta.total_hours,
                totalAmount: previewMeta.total_amount,
                hourlyRate,
                suggestedItem: previewMeta.suggested_item,
                dateCreated: previewMeta.date_created,
                dateDue: previewMeta.date_due,
                defaults: previewMeta.defaults || {},
              });
            }}
            className="px-4 py-2 text-sm font-semibold rounded-xl bg-complic-600 text-white hover:bg-complic-700 disabled:opacity-40"
          >
            Pokračovat na fakturu
          </button>
        </div>
      </div>
    </div>
  );
};

export default TimesheetModal;
